import {useState} from "react";
import ReactCalendar from "react-calendar";
import {ChevronBack, ChevronForward} from "@chayka/icons";
import {capitalizeFirstLetter, fromCalendarDate, isSameDay, toCalendarDate} from "@chayka/utils";
import clsx from "clsx";
import {ShadowBox} from "../ShadowBox";
import "./shared.css";
import "./index.css";

export interface CalendarProps {
  value?: Date;
  defaultValue?: Date;
  onChange?: (date: Date) => void;
  onMonthChange?: (weekStart: Date, weekEnd: Date) => void;
  className?: string;
  disabled?: boolean;
  highlightedDates?: Date[];
  minDate?: Date;
  maxDate?: Date;
}

export const Calendar: React.FC<CalendarProps> = ({
  value,
  defaultValue,
  onChange,
  onMonthChange,
  className,
  disabled,
  highlightedDates,
  minDate,
  maxDate,
}) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<Date | undefined>(defaultValue);
  const selectedDate = value ?? internalValue;
  const calendarValue = selectedDate ? toCalendarDate(selectedDate) : undefined;
  const calendarMinDate = minDate ? toCalendarDate(minDate) : undefined;
  const calendarMaxDate = maxDate ? toCalendarDate(maxDate) : undefined;

  const handleDateChange = (date: Date) => {
    const nextDate = fromCalendarDate(date);

    if (!isControlled) {
      setInternalValue(nextDate);
    }

    onChange?.(nextDate);
  };

  return (
    <ShadowBox
      className={clsx("calendar-surface p-4", disabled && "pointer-events-none opacity-60")}
    >
      <ReactCalendar
        locale="ru-RU"
        formatMonthYear={(locale, date) =>
          capitalizeFirstLetter(
            date.toLocaleString(locale, {month: "long", year: "numeric"}),
          ).replace("г.", "")
        }
        minDetail="month"
        maxDetail="month"
        prev2Label={null}
        next2Label={null}
        prevLabel={<ChevronBack size={18} />}
        nextLabel={<ChevronForward size={18} />}
        prevAriaLabel="Предыдущий месяц"
        nextAriaLabel="Следующий месяц"
        onActiveStartDateChange={({activeStartDate, view}) => {
          if (!activeStartDate || view !== "month") {
            return;
          }

          const monthStart = fromCalendarDate(
            new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1),
          );
          const monthEnd = fromCalendarDate(
            new Date(activeStartDate.getFullYear(), activeStartDate.getMonth() + 1, 0),
          );

          onMonthChange?.(monthStart, monthEnd);
        }}
        onClickDay={handleDateChange}
        value={calendarValue}
        className={clsx("calendar-root", className)}
        minDate={calendarMinDate}
        maxDate={calendarMaxDate}
        tileContent={({date, view}) => {
          const tileDate = fromCalendarDate(date);

          if (
            view === "month" &&
            highlightedDates?.some((highlightedDate) => isSameDay(highlightedDate, tileDate))
          ) {
            return (
              <span
                className={clsx(
                  "calendar-day-dot",
                  selectedDate && isSameDay(selectedDate, tileDate) && "calendar-day-dot--selected",
                )}
                aria-hidden="true"
              />
            );
          }
          return null;
        }}
        tileClassName={({date, view}) =>
          clsx(
            "calendar-day-button",
            view === "month" &&
              selectedDate &&
              isSameDay(selectedDate, fromCalendarDate(date)) &&
              "calendar-day-button--selected",
          )
        }
      />
    </ShadowBox>
  );
};
